import { ButtonDynamic, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useState } from "react"
import { useAsyncCallback } from "@nova/use-async-callback"
import {
    AnyAction,
    GenericUpload,
    ID,
    ImageProcessingState,
    SidePanelLayer,
    UploadPresentationOptions,
} from "@nova/constants"
import {
    activateFlightPath,
    addUploadTimestamp,
    deactivateFlightPath,
} from "features/map/layers/slice"
import { UploadState, useUploadSpeedForFlightId } from "@nova/slice-multifile-upload"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { useAddOnRestricted } from "features/data-management/workspace/subscriptions/hooks/useAddOnRestricted"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { PreviewFilesViewSwitcher } from "./PreviewFilesViewSwitcher"
import { AutoProcessToggle } from "panels/content/side-panel/upload-flow/components/AutoProcessToggle"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { GenericDataUsage } from "panels/content/side-panel/upload-flow/components/GenericDataUsage"
import { PreviewFilesUploads } from "./PreviewFilesUploads"
import { DeleteFlight } from "../components/DeleteFlight"
import { UploadWarningModal } from "./UploadWarningModal"
import { useRemoveSelectedUploads } from "./useRemoveSelectedUploads"
import { flightExceededUsage } from "../module"
import { PreviewFilesWarningsAndErrors } from "./PreviewFilesWarningsAndErrors"

export const GenericPreviewFiles = ({
    hasProcessingStep = true,
    uploadPresentationOptions = {
        completeStates: [ImageProcessingState.IngestDone],
        uploadingStates: [ImageProcessingState.UploadDone],
    },
}: {
    hasProcessingStep?: boolean
    uploadPresentationOptions?: UploadPresentationOptions
}) => {
    const {
        flightId,
        uploads,
        useProcessing,
        uploadFlight,
        minUploads = 3,
        usableUploads,
        addOnType,
        selectFlightById,
        selectIsUploading,
        selectFinishedUploadSize,
        selectUploadSize,
        selectUploadStateSummary,
        selectOverlapLayerIsEnabled,
        setOverlapLayer,
        secondaryPreviewAction,
        uploadFlowWarnings,
    } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const { isRestricted } = useAddOnRestricted(addOnType)
    const { removeSelected, isRemoving } = useRemoveSelectedUploads()

    const [viewMode, setViewMode] = useState<"Grid" | "List">("Grid")
    const [isShowingWarningModal, setIsShowingWarningModal] = useState(false)

    const flight = useAppSelector(selectFlightById(flightId))
    const averageUploadSpeed = useUploadSpeedForFlightId(flightId)
    const finishedUploadSize = useAppSelector(selectFinishedUploadSize(flightId))
    const uploadSize = useAppSelector(selectUploadSize(flightId))

    const { processCallback, dependencies } = useProcessing()
    const { createJob } = useAsyncCallback(
        {
            createJob: async () => {
                await processCallback()
                dispatch(
                    deactivateFlightPath({
                        flightId,
                    })
                )
            },
        },
        dependencies
    )

    const summary = useAppSelector(selectUploadStateSummary(flightId))
    const isUploading = useAppSelector(selectIsUploading(flightId))

    const canUpload = useMemo(
        () =>
            uploads.length >= minUploads &&
            uploads.every(
                (upload) => "state" in upload && upload.state !== ImageProcessingState.Processing
            ),
        [uploads, minUploads]
    )

    const canProcess = useMemo(
        () =>
            !!(
                canUpload &&
                uploads.every(
                    (upload) =>
                        "state" in upload &&
                        uploadPresentationOptions.completeStates.includes(upload.state)
                )
            ),
        [uploads, canUpload, uploadPresentationOptions.completeStates]
    )

    const stagingDone = useMemo(
        () =>
            !!usableUploads.length &&
            usableUploads.every((upload) => upload.state >= ImageProcessingState.ProcessingDone),
        [usableUploads]
    )

    useEffectUnsafe(() => {
        if (stagingDone) {
            dispatch(
                activateFlightPath({
                    flightId,
                    uploads: uploads as UploadState<{ id: string; error: Error }>[],
                    autoZoom: true,
                    useExistingPath: false,
                })
            )
        }
    }, [stagingDone, dispatch, flightId])

    let warningCount = 0
    if (!!uploadFlowWarnings?.[flightId])
        Object.values(uploadFlowWarnings[flightId]).forEach((warning) => {
            warningCount += warning.ids.length
        })

    const exceededUsage = flightExceededUsage[flightId]
    const hasExceededUploads = !isUploading && exceededUsage > 0
    const isPaywalled = hasExceededUploads || isRestricted

    const completedUploadCount = uploadPresentationOptions.completeStates.reduce(
        (sum, state) => sum + (summary.stateCount[state] ?? 0),
        0
    )

    const isReadOnly = isUploading || canProcess
    return (
        <>
            <PreviewFilesViewSwitcher viewMode={viewMode} setViewMode={setViewMode} />
            {/* Upload Info*/}
            {isUploading && (
                <div className="flex items-center justify-between">
                    {completedUploadCount > 0 ? (
                        <div className="flex items-center gap-1">
                            <h5 className="text-gray-0">Uploading</h5>
                            <h5 className="text-gray-100">
                                {`${completedUploadCount || 0} / ${summary.uploadCount}`}
                            </h5>
                        </div>
                    ) : (
                        <h5 className="text-gray-0">Upload Queued</h5>
                    )}
                    {completedUploadCount > 0 && (
                        <h5 className="text-gray-100">{averageUploadSpeed} Mbps</h5>
                    )}
                </div>
            )}
            {canProcess && (
                <div className="flex items-center gap-1">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Upload Complete</h5>
                </div>
            )}
            {hasExceededUploads && <GenericDataUsage remainingBytes={exceededUsage} />}
            {!isUploading && !canProcess && !isPaywalled && uploads.length >= minUploads && (
                <PreviewFilesWarningsAndErrors />
            )}
            {uploads.length < minUploads && (
                <h5 className="text-red-500 mt-2">
                    {`Add ${minUploads - uploads.length} more file${
                        minUploads - uploads.length === 1 ? "" : "s"
                    } to continue`}
                </h5>
            )}
            <div className="mt-2 flex flex-col min-h-0">
                {/* Grid View, List View, and Upload metrics */}
                <PreviewFilesUploads
                    viewMode={viewMode}
                    isPaywalled={isPaywalled}
                    removeSelected={removeSelected}
                    isRemoving={isRemoving}
                    uploadPresentationOptions={uploadPresentationOptions}
                />
                {!isUploading && setOverlapLayer && selectOverlapLayerIsEnabled && (
                    <OverlapLayerToggle
                        selectOverlapEnabled={selectOverlapLayerIsEnabled}
                        setOverlapLayer={setOverlapLayer}
                    />
                )}
                <InspectorPanelMetadata
                    entity={flight as SidePanelLayer}
                    uploads={uploads as GenericUpload[]}
                    uploadSize={uploadSize}
                    completedUploadCount={completedUploadCount || 0}
                    hideCreator
                    hideFileCount={finishedUploadSize > 0}
                    className=""
                />
                <div className="border-b border-gray-700" />
                {!isReadOnly && createJob && hasProcessingStep && (
                    <>
                        <AutoProcessToggle process={createJob} />
                        <div className="border-b border-gray-700" />
                    </>
                )}
                {secondaryPreviewAction && (
                    <ButtonDynamic
                        isTertiary={!canProcess && !isUploading}
                        title={secondaryPreviewAction.title}
                        RightIcon={Icons.ArrowRight}
                        onClick={() => secondaryPreviewAction.onClick(flightId)}
                        disabled={!stagingDone}
                        className="w-full mt-2"
                    />
                )}
                {/* action buttons */}
                <div className="flex gap-2 items-center mt-2">
                    <DeleteFlight />
                    {!isUploading && !canProcess && (
                        <ButtonPrimary
                            title={isUploading ? "Uploading..." : "Upload"}
                            className="w-full !px-0"
                            onClick={() => {
                                if (warningCount > 0) {
                                    setIsShowingWarningModal(true)
                                    return
                                }
                                dispatch(uploadFlight(flightId))
                                dispatch(
                                    addUploadTimestamp({
                                        flightId,
                                        timestamp: new Date().toISOString(),
                                    })
                                )
                            }}
                            disabled={!canUpload || isUploading || !isEditor || hasExceededUploads}
                        />
                    )}
                    {canProcess && hasProcessingStep && (
                        <ButtonPrimary
                            title="Process"
                            className="w-full !px-0"
                            onClick={createJob}
                            disabled={!canUpload || isUploading || !isEditor || hasExceededUploads}
                        />
                    )}
                </div>
            </div>
            {isShowingWarningModal && (
                <UploadWarningModal
                    onHideModal={() => setIsShowingWarningModal(false)}
                    removeSelected={removeSelected}
                    isRemoving={isRemoving}
                />
            )}
        </>
    )
}

const OverlapLayerToggle = ({
    selectOverlapEnabled,
    setOverlapLayer,
}: {
    setOverlapLayer: ({ id, active }: { id: ID; active: boolean }) => AnyAction
    selectOverlapEnabled: (flightId: string) => (state: AnyAction) => boolean
}) => {
    const { flightId } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const orthoOverlapIsEnabled = useAppSelector(selectOverlapEnabled(flightId))
    return (
        <InspectorPanelKeyValueToggle
            isToggled={!!orthoOverlapIsEnabled}
            setIsToggled={() =>
                dispatch(
                    setOverlapLayer!({
                        id: flightId,
                        active: !orthoOverlapIsEnabled,
                    })
                )
            }
            keyTitle="Show Overlap"
            className="my-3"
            toggleClassName="justify-between"
        />
    )
}
